/**
 * ABA分析详情页数据Hook
 * 职责：整合所有数据获取逻辑，提供统一接口
 */
import { useState, useEffect } from 'react';
import { useSKUData } from '../hooks/useSKUData';
import { useBrandNetwork } from '../hooks/useBrandNetwork';
import { useMarketMatrix } from '../hooks/useMarketMatrix';
import { useSKUActions } from '../hooks/useSKUActions';
import { useSearchPerformance } from '../hooks/useSearchPerformance';

export const useABAAnalysisData = (id: string | undefined) => {
    // 1. 基础SKU数据
    const {
        loading,
        sku,
        periods,
        keywords,
        competitors,
        summary,
        loadData,
        reload,
    } = useSKUData(id);

    // 2. 品牌网络数据
    const {
        networkData,
        brandDetails,
        networkLoading,
        brandDetailsLoading,
        fetchNetworkData,
        generateBrandNetwork,
        fetchBrandDetails,
    } = useBrandNetwork(id);

    // 3. 市场矩阵数据
    const {
        matrixData,
        rankRangeData,
        brandShiftData,
        loading: matrixLoading,
        fetchData: fetchMarketMatrixData,
    } = useMarketMatrix(id);

    // 4. SKU操作
    const {
        handleExport,
        handleExportEnhancedExcel,
        handleDelete,
    } = useSKUActions(id, sku, periods.length, reload);

    // 5. 搜索查询绩效数据
    const {
        periods: spPeriods,
        advantageData,
        marketOverview,
        opportunityData,
        keywordPivotData,
        loading: spLoading,
        fetchAdvantageKeywords,
        fetchMarketOverview,
        fetchOpportunityKeywords,
        fetchKeywordPivot,
    } = useSearchPerformance(Number(id));

    // 6. 本地状态
    const [spSelectedPeriodId, setSpSelectedPeriodId] = useState<number | null>(null);

    // 初始化选中的周期ID
    useEffect(() => {
        if (spPeriods.length > 0 && !spSelectedPeriodId) {
            setSpSelectedPeriodId(spPeriods[0].id);
        }
    }, [spPeriods, spSelectedPeriodId]);

    // 初始加载SKU数据
    useEffect(() => {
        if (id) {
            loadData();
        }
    }, [id, loadData]);

    // 自动加载关键词透视数据
    useEffect(() => {
        if (spPeriods.length > 0 && !keywordPivotData) {
            fetchKeywordPivot();
        }
    }, [spPeriods, keywordPivotData, fetchKeywordPivot]);

    // Tab切换处理
    const handleTabChange = (key: string) => {
        if (key === 'brand-competition' && !networkData) {
            fetchNetworkData();
        }
        if ((key === 'market-matrix' || key === 'rank-range' || key === 'brand-shift') && !matrixData) {
            fetchMarketMatrixData();
        }
        // 搜索查询绩效Tab懒加载
        if (key === 'sp-advantage' && !advantageData && spPeriods.length > 0) {
            fetchAdvantageKeywords({ period_ids: [spPeriods[0].id] });
        }
        if (key === 'sp-overview' && !marketOverview && spPeriods.length > 0) {
            fetchMarketOverview({ period_ids: [spPeriods[0].id] });
        }
        if (key === 'sp-opportunity' && !opportunityData && spPeriods.length > 0) {
            fetchOpportunityKeywords({ period_ids: [spPeriods[0].id] });
        }
    };

    return {
        // 基础数据
        loading,
        sku,
        periods,
        keywords,
        competitors,
        summary,
        reload,

        // 品牌网络
        networkData,
        brandDetails,
        networkLoading,
        brandDetailsLoading,
        generateBrandNetwork,
        fetchBrandDetails,

        // 市场矩阵
        matrixData,
        rankRangeData,
        brandShiftData,
        matrixLoading,

        // 操作
        handleExport,
        handleExportEnhancedExcel,
        handleDelete,

        // 搜索查询绩效
        spPeriods,
        spSelectedPeriodId,
        advantageData,
        marketOverview,
        opportunityData,
        keywordPivotData,
        spLoading,

        // 事件处理
        handleTabChange
    };
};
